import { AngleLeftLightGlyph, AngleRightLightGlyph } from '@acast-tech/decibel-glyphs';
import T from 'prop-types';
import styled from 'styled-components';
import { numberHigherThan } from '../../helpers/propTypes';
import { black32, purple, purple32, purple8, purpleAlpha56, white } from '../../styles/colors';
import { getFocusRing, resetButtonStyle } from '../../styles/mixins';
import { body2 } from '../../styles/typography';
import { unit } from '../../styles/unit';
import { Icon } from '../Icon/Icon';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PaginationList = /*#__PURE__*/styled.ul.withConfig({
  displayName: "PaginationList",
  componentId: "zcqbs4-0"
})(["display:flex;padding:0;justify-content:center;"]);
const PageItem = /*#__PURE__*/styled.li.withConfig({
  displayName: "PageItem",
  componentId: "zcqbs4-1"
})(["list-style-type:none;margin:0 ", ";user-select:none;& > *{display:flex;align-items:center;justify-content:center;}"], unit(1));
const PageItemContent = /*#__PURE__*/styled.button.withConfig({
  displayName: "PageItemContent",
  componentId: "zcqbs4-2"
})(["", " ", " height:24px;min-width:24px;border-radius:4px;outline:none;cursor:pointer;:hover{background-color:", ";}&.focus-visible{box-shadow:", ";}:active{background-color:", ";}&[aria-disabled='true']{color:", ";cursor:default;:hover{background-color:transparent;}&.focus-visible{box-shadow:none;}}&[aria-current='true'],&[aria-current='page']{color:", ";background-color:", ";&[aria-disabled='true']{background-color:", ";}}"], resetButtonStyle, body2, purple8, getFocusRing(purpleAlpha56), purple32, black32, white, purple, black32);
const PageItemNumber = /*#__PURE__*/styled.span.withConfig({
  displayName: "PageItemNumber",
  componentId: "zcqbs4-3"
})(["padding:0 ", ";"], unit(1));
const PageItemEllipsis = /*#__PURE__*/styled.span.withConfig({
  displayName: "PageItemEllipsis",
  componentId: "zcqbs4-4"
})(["height:24px;min-width:24px;:after{content:'...';}&[aria-disabled='true']{color:", ";}"], black32);

const noop = () => {};

const Pagination = ({
  total: unformattedTotal,
  onPageChange = noop,
  current = 1,
  innerRange = 2,
  outerRange = 2,
  disabled = false
}) => {
  const total = Math.ceil(unformattedTotal);

  const handleOnChange = pageNum => {
    const isAllowedToChangePage = !disabled && pageNum >= 1 && pageNum <= total;

    if (isAllowedToChangePage) {
      onPageChange(pageNum);
    }
  };

  function renderItem(pageNum) {
    const isCurrent = current === pageNum;
    return /*#__PURE__*/_jsx(PageItem, {
      children: /*#__PURE__*/_jsx(PageItemContent, {
        onClick: () => handleOnChange(pageNum),
        tabIndex: disabled ? -1 : 0,
        "aria-current": isCurrent && 'page',
        "aria-label": isCurrent ? `Current page, page ${pageNum}` : `Go to page ${pageNum}`,
        "aria-disabled": disabled,
        children: /*#__PURE__*/_jsx(PageItemNumber, {
          children: pageNum
        })
      })
    }, pageNum);
  }

  function renderRange(start, end) {
    if (start <= 0 || start > end || start > total) {
      return [];
    }

    const count = end - start;
    const result = [];

    for (let index = 0; index < count + 1; index += 1) {
      const pageNum = start + index;
      result.push(renderItem(pageNum));
    }

    return result;
  }

  function renderEllipsisOrPage(prevPage, nextPage) {
    if (prevPage >= nextPage || prevPage + 1 === nextPage || outerRange <= 0 || outerRange * 2 > total) {
      return null;
    }

    if (prevPage + 2 === nextPage) {
      return renderItem(prevPage + 1);
    }

    return /*#__PURE__*/_jsx(PageItem, {
      children: /*#__PURE__*/_jsx(PageItemEllipsis, {
        "aria-disabled": disabled
      })
    });
  }

  function renderInnerRange(firstRangeEnd, lastRangeStart) {
    if (firstRangeEnd >= lastRangeStart || firstRangeEnd + 1 === lastRangeStart) {
      return null;
    }

    const start = Math.max(Math.min(current - innerRange, lastRangeStart - 2 * innerRange - 2), firstRangeEnd + 2);
    const end = Math.min(start + 2 * innerRange, lastRangeStart - 1);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderEllipsisOrPage(firstRangeEnd, start), renderRange(start, end), renderEllipsisOrPage(end, lastRangeStart)]
    });
  }

  const disablePrev = disabled || current <= 1;
  const disableNext = disabled || current >= total;
  const firstRangeEnd = Math.min(outerRange, total);
  const lastRangeStart = Math.max(total - outerRange + 1, firstRangeEnd + 1);
  return /*#__PURE__*/_jsxs(PaginationList, {
    children: [/*#__PURE__*/_jsx(PageItem, {
      children: /*#__PURE__*/_jsx(PageItemContent, {
        onClick: () => handleOnChange(current - 1),
        tabIndex: disablePrev ? -1 : 0,
        "aria-disabled": disablePrev,
        "aria-label": "Go to previous page",
        children: /*#__PURE__*/_jsx(Icon, {
          glyph: AngleLeftLightGlyph
        })
      })
    }), renderRange(1, firstRangeEnd), renderInnerRange(firstRangeEnd, lastRangeStart), renderRange(lastRangeStart, total), /*#__PURE__*/_jsx(PageItem, {
      children: /*#__PURE__*/_jsx(PageItemContent, {
        onClick: () => handleOnChange(current + 1),
        tabIndex: disableNext ? -1 : 0,
        "aria-disabled": disableNext,
        "aria-label": "Go to next page",
        children: /*#__PURE__*/_jsx(Icon, {
          glyph: AngleRightLightGlyph
        })
      })
    })]
  });
};

Pagination.propTypes = {
  total: T.number.isRequired,
  onPageChange: T.func.isRequired,
  current: numberHigherThan(0),
  innerRange: numberHigherThan(0),
  outerRange: numberHigherThan(0),
  disabled: T.bool
};
export { Pagination };